import { useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Info, Check } from "lucide-react";
import { toast } from "sonner";

// KPI templates grouped by industry. Each template is a complete KPI payload
// (scope=company, is_active=true, manual source) that the customer can tick
// and create in one click. Customers can still hand-craft a custom KPI from
// the New KPI dialog -- this is purely a starting-point catalogue.
// Slugs are intentionally stable across industries so the same concept
// (e.g. `absence_pct`) is the same KPI regardless of where the customer
// picked it from. We de-duplicate by slug before submitting.

const TEMPLATES = {
  customer_support: {
    label: "Customer Support",
    description: "Calls, emails, chats, SLA and customer satisfaction.",
    items: [
      { key: "calls_handled",      name: "Calls handled",          unit: "count",   direction: "higher_is_better", description: "Total inbound and outbound calls handled in the period." },
      { key: "emails_handled",     name: "Emails handled",         unit: "count",   direction: "higher_is_better", description: "Total customer emails handled in the period." },
      { key: "chats_handled",      name: "Live chats handled",     unit: "count",   direction: "higher_is_better", description: "Total live chat conversations handled in the period." },
      { key: "first_response_hrs", name: "First response time",    unit: "hours",   direction: "lower_is_better",  description: "Average time from contact received to first agent response." },
      { key: "sla_pct",            name: "SLA %",                  unit: "percent", direction: "higher_is_better", description: "Percentage of contacts answered within the agreed service-level target." },
      { key: "csat_pct",           name: "Customer satisfaction (CSAT)", unit: "percent", direction: "higher_is_better", description: "Average customer satisfaction score, expressed as a percentage." },
      { key: "absence_pct",        name: "Absence %",              unit: "percent", direction: "lower_is_better",  description: "Percentage of scheduled hours lost to unplanned absence." },
      { key: "overtime_hours",     name: "Overtime hours",         unit: "hours",   direction: "lower_is_better",  description: "Total hours worked above the standard schedule." },
      { key: "weekend_balance",    name: "Weekend balance",        unit: "count",   direction: "target_band",      description: "How evenly weekend shifts are distributed across the team." },
    ],
  },
  warehouse: {
    label: "Warehouse / Logistics",
    description: "Picking, shipping, accuracy and productivity.",
    items: [
      { key: "orders_picked",      name: "Orders picked",          unit: "count",   direction: "higher_is_better", description: "Total orders picked in the period." },
      { key: "orders_shipped",     name: "Orders shipped",         unit: "count",   direction: "higher_is_better", description: "Total orders dispatched in the period." },
      { key: "picking_accuracy",   name: "Picking accuracy %",     unit: "percent", direction: "higher_is_better", description: "Percentage of picks completed without an error." },
      { key: "dispatch_accuracy",  name: "Dispatch accuracy %",    unit: "percent", direction: "higher_is_better", description: "Percentage of dispatches sent without a routing or labelling error." },
      { key: "overtime_hours",     name: "Overtime hours",         unit: "hours",   direction: "lower_is_better",  description: "Total hours worked above the standard schedule." },
      { key: "absence_pct",        name: "Absence %",              unit: "percent", direction: "lower_is_better",  description: "Percentage of scheduled hours lost to unplanned absence." },
      { key: "productivity_per_emp", name: "Productivity per employee", unit: "count", direction: "higher_is_better", description: "Average units (picks, packs or shipments) per employee per shift." },
    ],
  },
  healthcare: {
    label: "Healthcare",
    description: "Patient contacts, waiting times and shift coverage.",
    items: [
      { key: "patient_contacts",   name: "Patient contacts",       unit: "count",   direction: "higher_is_better", description: "Total patient interactions in the period." },
      { key: "avg_waiting_time",   name: "Average waiting time",   unit: "hours",   direction: "lower_is_better",  description: "Average wait time from arrival to first clinician contact." },
      { key: "staff_utilisation_pct", name: "Staff utilisation %", unit: "percent", direction: "target_band",      description: "Percentage of available staff time spent on direct patient care." },
      { key: "overtime_hours",     name: "Overtime hours",         unit: "hours",   direction: "lower_is_better",  description: "Total hours worked above the standard schedule." },
      { key: "absence_pct",        name: "Absence %",              unit: "percent", direction: "lower_is_better",  description: "Percentage of scheduled hours lost to unplanned absence." },
      { key: "shift_coverage_pct", name: "Shift coverage %",       unit: "percent", direction: "higher_is_better", description: "Percentage of required shifts that were filled." },
    ],
  },
  retail: {
    label: "Retail",
    description: "Sales, transactions and labour cost.",
    items: [
      { key: "sales_revenue",      name: "Sales revenue",          unit: "currency", currency_code: "GBP", direction: "higher_is_better", description: "Total sales revenue in the period." },
      { key: "transactions",       name: "Transactions completed", unit: "count",   direction: "higher_is_better", description: "Total customer transactions in the period." },
      { key: "avg_basket_value",   name: "Average basket value",   unit: "currency", currency_code: "GBP", direction: "higher_is_better", description: "Average value per customer transaction." },
      { key: "labour_cost_pct",    name: "Labour cost %",          unit: "percent", direction: "lower_is_better",  description: "Labour cost expressed as a percentage of revenue." },
      { key: "absence_pct",        name: "Absence %",              unit: "percent", direction: "lower_is_better",  description: "Percentage of scheduled hours lost to unplanned absence." },
      { key: "staff_utilisation_pct", name: "Staff utilisation %", unit: "percent", direction: "target_band",      description: "Percentage of paid staff time used on planned activity." },
    ],
  },
  general: {
    label: "General Workforce",
    description: "Universal metrics most teams care about.",
    items: [
      { key: "absence_pct",        name: "Absence %",              unit: "percent", direction: "lower_is_better",  description: "Percentage of scheduled hours lost to unplanned absence." },
      { key: "overtime_hours",     name: "Overtime hours",         unit: "hours",   direction: "lower_is_better",  description: "Total hours worked above the standard schedule." },
      { key: "weekend_balance",    name: "Weekend balance",        unit: "count",   direction: "target_band",      description: "How evenly weekend shifts are distributed across the team." },
      { key: "shift_coverage_pct", name: "Shift coverage %",       unit: "percent", direction: "higher_is_better", description: "Percentage of required shifts that were filled." },
      { key: "employee_util_pct",  name: "Employee utilisation %", unit: "percent", direction: "target_band",      description: "Percentage of paid time spent on productive work." },
      { key: "annual_leave_usage", name: "Annual leave usage",     unit: "percent", direction: "target_band",      description: "Percentage of annual leave entitlement taken so far." },
      { key: "training_complete_pct", name: "Training completion %", unit: "percent", direction: "higher_is_better", description: "Percentage of required training completed across the team." },
    ],
  },
};

const GROUPS = Object.keys(TEMPLATES);

export default function KPITemplatePicker({ open, onOpenChange, onCreated,
                                            existingKeys = [], hiddenKeys = null,
                                            mode = "kpi" }) {
  // Phase 2Z.s-1 — Performance Focus polish. When mode="performance"
  // the toast wording switches from "KPI/KPIs" to "activity/activities"
  // and every created definition is tagged so the Performance Focus
  // page can find it even when target_value / linked_category_id
  // are still blank ("Needs setup" rows).
  const isPerformance = mode === "performance";
  const noun = isPerformance ? "activity" : "KPI";
  const nounPlural = isPerformance ? "activities" : "KPIs";

  const [tab, setTab] = useState("general");
  const [selected, setSelected] = useState({}); // { [key]: template }
  const [busy, setBusy] = useState(false);

  const existingSet = useMemo(() => new Set(existingKeys), [existingKeys]);
  // Phase 2Z.s — optional hide-list (e.g. the Performance Focus
  // page hides absence/overtime/weekend templates). Accepts a
  // Set or array.
  const hiddenSet = useMemo(() => {
    if (!hiddenKeys) return new Set();
    return hiddenKeys instanceof Set ? hiddenKeys : new Set(hiddenKeys);
  }, [hiddenKeys]);

  const toggle = (tpl) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[tpl.key]) delete next[tpl.key];
      else next[tpl.key] = tpl;
      return next;
    });
  };

  const selectedList = Object.values(selected);

  const create = async () => {
    if (selectedList.length === 0) {
      toast.error("Pick at least one template to create");
      return;
    }
    setBusy(true);
    let ok = 0, skipped = 0, failed = 0;
    for (const tpl of selectedList) {
      if (existingSet.has(tpl.key)) { skipped += 1; continue; }
      try {
        await api.post("/kpis", {
          key: tpl.key,
          name: tpl.name,
          description: tpl.description,
          unit: tpl.unit,
          currency_code: tpl.unit === "currency" ? (tpl.currency_code || "GBP") : null,
          direction: tpl.direction,
          target_value: null,
          warning_threshold: null,
          critical_threshold: null,
          scope: "company",
          scope_id: null,
          // Performance Focus tags every template-created activity
          // so the page lists them immediately (even before the
          // manager fills in target/link). Legacy KPI flow stays
          // untagged.
          tags: isPerformance ? ["performance_focus"] : [],
          is_active: true,
          allowed_sources: ["manual"],
          preferred_source: "manual",
        });
        ok += 1;
      } catch (err) {
        const msg = formatError(err.response?.data?.detail) || "";
        // Backend rejects duplicate keys, treat as "already exists" silently.
        if (/already exists|duplicate/i.test(msg)) skipped += 1;
        else failed += 1;
      }
    }
    setBusy(false);
    if (ok > 0) toast.success(`Added ${ok} ${ok === 1 ? noun : nounPlural}${skipped ? ` (${skipped} already existed)` : ""}`);
    else if (skipped > 0 && failed === 0) toast.info(`All ${skipped} selected ${nounPlural} already exist`);
    if (failed > 0) toast.error(`${failed} ${failed === 1 ? noun : nounPlural} could not be created`);
    setSelected({});
    onCreated?.();
    if (ok > 0 || skipped > 0) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" fullscreenOnMobile data-testid="kpi-template-picker">
        <DialogHeader>
          <DialogTitle>Start from a template</DialogTitle>
          <DialogDescription>
            Tick the metrics you&apos;d like to track. NextRota creates the KPI definitions
            for you, you can fine-tune the targets and thresholds later. You can also
            create a fully custom KPI from the <strong>New KPI</strong> button.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-2 p-3 rounded-lg bg-[#F6F9FE] border border-[#DBE7FE] text-xs text-[#1F2A44]"
          data-testid="kpi-template-helper">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#2563EB]" />
          <div>
            All templates create <strong>company-wide</strong> KPIs with manual entry.
            You can change the scope, targets and tags any time after creation.
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full mt-3">
          <TabsList className="bg-[#F6F9FE] border border-slate-200 h-9 p-1 mb-3 flex-wrap"
            data-testid="kpi-template-tabs">
            {GROUPS.map((g) => (
              <TabsTrigger key={g} value={g} data-testid={`kpi-template-tab-${g}`}
                className="data-[state=active]:bg-white data-[state=active]:text-[#1D4ED8] rounded-md px-3">
                {TEMPLATES[g].label}
              </TabsTrigger>
            ))}
          </TabsList>

          {GROUPS.map((g) => (
            <TabsContent key={g} value={g}>
              <p className="text-xs text-[#4B5773] mb-3">{TEMPLATES[g].description}</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[360px] overflow-auto pr-1"
                  data-testid={`kpi-template-list-${g}`}>
                {TEMPLATES[g].items.filter((tpl) => !hiddenSet.has(tpl.key)).map((tpl) => {
                  const isSelected = !!selected[tpl.key];
                  const alreadyExists = existingSet.has(tpl.key);
                  return (
                    <li key={tpl.key}>
                      <label
                        data-testid={`kpi-template-item-${tpl.key}`}
                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          isSelected
                            ? "bg-[#F0F6FF] border-[#2563EB]"
                            : alreadyExists
                              ? "bg-slate-50 border-slate-200 opacity-70"
                              : "bg-white border-slate-200 hover:border-[#2563EB]"
                        }`}>
                        <input type="checkbox"
                          checked={isSelected}
                          disabled={alreadyExists}
                          onChange={() => toggle(tpl)}
                          className="mt-0.5 h-4 w-4 accent-[#2563EB]" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-sm font-medium text-[#0B1A36]">{tpl.name}</span>
                            <span className="text-[10px] uppercase tracking-widest text-[#4B5773] px-1.5 py-0.5 rounded bg-slate-100">
                              {tpl.unit}
                            </span>
                            {alreadyExists && (
                              <span className="text-[10px] uppercase tracking-widest text-emerald-700 px-1.5 py-0.5 rounded bg-emerald-50 inline-flex items-center gap-0.5">
                                <Check className="h-2.5 w-2.5" />already added
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-[#4B5773] mt-1 leading-snug">{tpl.description}</p>
                        </div>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </TabsContent>
          ))}
        </Tabs>

        <DialogFooter className="mt-4 flex items-center justify-between gap-2 sm:flex-row">
          <div className="text-xs text-[#4B5773]" data-testid="kpi-template-count">
            {selectedList.length === 0
              ? "Tick the templates you\u2019d like to create."
              : `${selectedList.length} template${selectedList.length === 1 ? "" : "s"} selected`}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}
              data-testid="kpi-template-cancel" className="rounded-lg">
              Cancel
            </Button>
            <Button onClick={create} disabled={busy || selectedList.length === 0}
              data-testid="kpi-template-create"
              className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
              {busy ? "Creating…" : `Create ${selectedList.length || ""} selected`.trim()}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
